// Leaflet location picker — click on map to set GPS, search address via Nominatim (OpenStreetMap).
// Defaults to Esri Satellite layer (with road/place labels overlay) — operators recognise scenes visually.
import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, MapPin, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

// Fix default Leaflet marker icons under bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [-26.107567, 28.056702]; // Sandton, ZA
const DEFAULT_ZOOM = 11;

// Nominatim "type" / "class" ranking — we strongly prefer settled places over farms/lone localities.
const PLACE_RANK = {
  city: 0, town: 1, village: 2, suburb: 3, neighbourhood: 4, hamlet: 5,
  administrative: 6, residential: 7, locality: 8, farm: 9, isolated_dwelling: 10,
};

const rankResult = (r) => {
  const t = r.type || r.class || '';
  return PLACE_RANK[t] ?? 99;
};

const MapClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

const Recenter = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.flyTo([lat, lng], 15, { duration: 0.6 });
  }, [lat, lng, map]);
  return null;
};

export const LocationPicker = ({ address, lat, lng, onChange, readOnly = false, height = 320 }) => {
  const [searchQuery, setSearchQuery] = useState(address || '');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef();

  useEffect(() => { setSearchQuery(address || ''); }, [address]);

  // Strategy: fetch up to 8 ZA results, sort by place-type rank, then pick the best.
  // Two-stage: first try exact query, then retry appending ", South Africa".
  const search = async (q) => {
    if (!q || q.trim().length < 3) return;
    setBusy(true);
    const tryQuery = async (text) => {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&limit=8&countrycodes=za&addressdetails=1`,
      );
      return res.json();
    };
    try {
      let data = await tryQuery(q);
      if (!data || data.length === 0) data = await tryQuery(`${q}, South Africa`);
      if (data && data.length > 0) {
        // Pick the best-ranked result (cities/towns/suburbs beat farms)
        const best = [...data].sort((a, b) => rankResult(a) - rankResult(b))[0];
        const newLat = parseFloat(best.lat);
        const newLng = parseFloat(best.lon);
        onChange?.({ address: best.display_name, lat: newLat, lng: newLng });
      } else {
        toast.error('No results found for that address');
      }
    } catch (e) {
      toast.error('Address lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 600);
  };

  const reverseGeocode = async (newLat, newLng) => {
    onChange?.({ lat: newLat, lng: newLng, address }); // immediate update
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLat}&lon=${newLng}&zoom=18`
      );
      const data = await res.json();
      if (data?.display_name) {
        onChange?.({ lat: newLat, lng: newLng, address: data.display_name });
        setSearchQuery(data.display_name);
      }
    } catch { /* non-blocking */ }
  };

  const handleCopy = () => {
    if (lat == null || lng == null) return;
    const txt = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    if (navigator.clipboard) navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const center = lat != null && lng != null ? [lat, lng] : DEFAULT_CENTER;

  return (
    <div className="space-y-2" data-testid="location-picker">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder={readOnly ? 'No address' : 'Search address (e.g. Centurion, or 15 Rivonia Rd Sandton)'}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
          disabled={readOnly}
          data-testid="location-search-input"
        />
        {busy && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">searching...</span>}
      </div>

      <div className="rounded-md overflow-hidden border border-slate-200 relative" style={{ height }}>
        <MapContainer
          center={center}
          zoom={lat != null ? 15 : DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Satellite">
              <TileLayer
                attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Streets">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay checked name="Place & Road Labels">
              <TileLayer
                attribution='Reference &copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.Overlay>
          </LayersControl>
          {lat != null && lng != null && <Marker position={[lat, lng]} />}
          {!readOnly && <MapClickHandler onPick={reverseGeocode} />}
          <Recenter lat={lat} lng={lng} />
        </MapContainer>
      </div>

      <div className="flex items-center gap-2 px-1">
        <MapPin className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs text-slate-500 font-mono flex-1">
          {lat != null && lng != null
            ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
            : 'Click the map or search above to set a location'}
        </span>
        {lat != null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={handleCopy}
            data-testid="location-copy-btn"
          >
            {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </div>
    </div>
  );
};
